import { Button, Divider, Flex, IconFont, Text } from '@byecode/ui'
import type { OtherPlatformType } from '@lighthouse/shared'
import { spaceVersionEnum, TagIcon, useAtomData, useSpaceQuotaContext } from '@lighthouse/shared'
import React from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { SpaceGradeTag } from '@/components/SpaceGrade'
import { useGetAiModelList } from '@/shared/reusable'

import {
    advancedOtherPlatFormList,
    AI_INTEGRATION_TYPE_LIST,
    INTEGRATION_LIST,
    NO_AUTH_INTEGRATION_TYPE_LIST,
    otherPlatformBackgroundColorMap,
    otherPlatformColorMap,
    otherPlatformDescribeMap,
    otherPlatformIconMap,
    otherPlatformNameMap
} from '../constant'
import { OtherPlatformTags } from '../Tags'
import * as SC from './styles'

interface OtherPlatformPanelProps {
    type: OtherPlatformType
    onDelete: (type: OtherPlatformType) => void
    onPerfect: (type: OtherPlatformType) => void
    onSelect: () => void
}

export const OtherPlatformPanel: React.FunctionComponent<OtherPlatformPanelProps> = ({ type, onDelete, onPerfect, onSelect }) => {
    const data = INTEGRATION_LIST.flatMap(item => item.list).find(item => item.type === type)
    const otherPlatforms = useAtomData(otherPlatformListAtom)
    const { quota: spaceQuota } = useSpaceQuotaContext()
    const integration = otherPlatforms?.find(v => v.type === type)

    const { data: aiModuleList } = useGetAiModelList()

    const moduleList = aiModuleList?.find(v => v.type === type)?.modules ?? []

    return (
        <SC.Container>
            <SC.Header>
                <SC.CardInfo>
                    <SC.Info>
                        <TagIcon
                            radius={6}
                            size={40}
                            iconSize={24}
                            iconColor={otherPlatformColorMap[type]}
                            icon={otherPlatformIconMap[type]}
                            background={otherPlatformBackgroundColorMap[type]}
                        />
                        <Flex direction="column" justifyContent="space-between" style={{ flex: 1, height: 40, overflow: 'hidden' }}>
                            <Text size={14} lineHeight="14px">
                                {otherPlatformNameMap[type]}
                            </Text>
                            <OtherPlatformTags data={data?.tags ?? []} />
                        </Flex>
                    </SC.Info>
                    <Flex style={{ height: '100%' }} gap={6}>
                        {type !== 'WECHAT_PAY' && (
                            <TagIcon
                                size={24}
                                iconSize={16}
                                iconColor="var(--color-gray-400)"
                                icon="Trash"
                                radius={4}
                                enableHover
                                hoverBackground="var(--color-gray-100)"
                                onClick={() => {
                                    onDelete(type)
                                }}
                            />
                        )}
                        <TagIcon
                            radius={100}
                            size={24}
                            iconSize={16}
                            iconColor="var(--color-gray-400)"
                            icon="Close"
                            background="var(--color-gray-100)"
                            onClick={() => {
                                onSelect()
                            }}
                        />
                    </Flex>
                </SC.CardInfo>
                <Text lineHeight="20px" whiteSpace="pre-wrap" size={12} color="var(--color-gray-500)">
                    {otherPlatformDescribeMap[type]}
                </Text>
                {NO_AUTH_INTEGRATION_TYPE_LIST.includes(type) ? null : integration?.configurationComplete ? (
                    <Button
                        size="lg"
                        style={{ marginTop: 16, position: 'relative' }}
                        icon={<IconFont size={16} type="TickCircle" color="var(--color-green-500)" />}
                        block
                        onClick={() => onPerfect(type)}
                    >
                        {type === 'WECHAT_PAY' ? '管理收款商户' : '已授权'}
                        {advancedOtherPlatFormList.includes(type) &&
                            (spaceQuota?.currentVersionCode ?? spaceVersionEnum.FREE) < spaceVersionEnum.ADVANCED && (
                                <SpaceGradeTag
                                    style={{
                                        position: 'absolute',
                                        top: 7,
                                        right: 7,
                                        zIndex: 10
                                    }}
                                    value={spaceVersionEnum.ADVANCED}
                                />
                            )}
                    </Button>
                ) : (
                    <Button type="primary" size="lg" style={{ marginTop: 16, position: 'relative' }} block onClick={() => onPerfect(type)}>
                        {type === 'WECHAT_PAY' ? '管理收款商户' : '完善授权'}
                        {advancedOtherPlatFormList.includes(type) &&
                            (spaceQuota?.currentVersionCode ?? spaceVersionEnum.FREE) < spaceVersionEnum.ADVANCED && (
                                <SpaceGradeTag
                                    style={{
                                        position: 'absolute',
                                        top: 7,
                                        right: 7,
                                        zIndex: 10
                                    }}
                                    value={spaceVersionEnum.ADVANCED}
                                />
                            )}
                    </Button>
                )}
            </SC.Header>
            <Divider />
            <SC.Content>
                {(data?.capabilityList ?? []).length > 0 && (
                    <Flex>
                        {integration?.configurationComplete || NO_AUTH_INTEGRATION_TYPE_LIST.includes(type) ? (
                            <Text size={12} lineHeight="20px" color="var(--color-gray-500)">
                                包含 {data?.capabilityList?.length} 个能力
                            </Text>
                        ) : (
                            <>
                                <Text size={12} lineHeight="20px" color="var(--color-gray-500)">
                                    包含 {data?.capabilityList?.length} 个能力，
                                </Text>
                                <Text size={12} lineHeight="20px" color="var(--color-purple-500)" onClick={() => onPerfect(type)}>
                                    使用前请先完善授权
                                </Text>
                            </>
                        )}
                    </Flex>
                )}
                {data?.capabilityList?.map((item, index) => {
                    return (
                        <SC.Capability key={item.title}>
                            <SC.CapabilityTitle>{item.title}</SC.CapabilityTitle>
                            <SC.CapabilityDescribe>{item.description}</SC.CapabilityDescribe>
                        </SC.Capability>
                    )
                })}

                {(data?.featureList ?? []).length > 0 && (
                    <Flex style={{ marginTop: 12 }}>
                        {integration?.configurationComplete ? (
                            <Text size={12} lineHeight="20px" color="var(--color-gray-500)">
                                是以下 {data?.featureList?.length} 个功能的前提条件：
                            </Text>
                        ) : (
                            <>
                                <Text size={12} lineHeight="20px" color="var(--color-gray-500)">
                                    是以下 {data?.featureList?.length} 个功能的前提条件：
                                </Text>
                                <Text size={12} lineHeight="20px" color="var(--color-purple-500)" onClick={() => onPerfect(type)}>
                                    使用前请先完善授权
                                </Text>
                            </>
                        )}
                    </Flex>
                )}
                {data?.featureList?.map((item, index) => {
                    return (
                        <SC.Capability key={item.title}>
                            <SC.CapabilityTitle>{item.title}</SC.CapabilityTitle>
                            <SC.CapabilityDescribe>{item.describe}</SC.CapabilityDescribe>
                        </SC.Capability>
                    )
                })}
                {AI_INTEGRATION_TYPE_LIST.includes(type) && integration?.configurationComplete && (
                    <>
                        {moduleList.length > 0 && (
                            <Text size={12} style={{ marginTop: 12 }} lineHeight="20px" color="var(--color-gray-500)">
                                包含 {moduleList?.length} 个模型：
                            </Text>
                        )}
                        {moduleList.map((item, index) => {
                            return (
                                <SC.Capability key={item.value}>
                                    <SC.CapabilityTitle>{item.label}</SC.CapabilityTitle>
                                    {/* ai模型的tag  等待后端返回tag */}
                                    {/* <OtherPlatformTags data={[]} /> */}
                                </SC.Capability>
                            )
                        })}
                    </>
                )}
            </SC.Content>
        </SC.Container>
    )
}
